<template>
  <VRow no-gutters>
    <VCol cols="12">
      <VRow no-gutters>
        <VCol cols="12">
          <VTextarea
            ref="textarea"
            v-model="template"
            outlined
            hide-details
            :error="error"
          />
          <span
            class="smsCounter text-caption"
            :class="error ? 'exceed' : 'normal'"
            v-if="smsCharactersCount && smsCharactersCount > 0"
          >
            {{ $t('usedSymbolLimit', { smsCharactersCount: smsCharactersCount, smsCharactersLimit: smsCharactersLimit }) }}
          </span>
        </VCol>
        <VCol col="12">
          <div class="font-weight-medium my-2">
            {{ $t('insertVariable') }}
          </div>
          <div />
          <VRow no-gutters>
            <VCol
              v-for="variable of vars"
              :key="variable.id"
              cols="6"
            >
              <div
                class="d-flex align-center"
              >
                <span>&uarr;</span>
                <div
                  class="ml-3 pseudo-link primary--text var-item"
                  @click="insertVariableAtCursorPosition(variable)"
                >
                  {{ variable.name }}
                </div>
              </div>
            </VCol>
          </VRow>
        </VCol>
      </VRow>
    </VCol>
  </VRow>
</template>

<script>
import { notificationChannelType } from '@/enums';
import { getVariableValue } from '@/functions';
import {
  computed, nextTick, ref, watch, toRefs,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import { useSms } from '@/compositions/useSms';
import useUser from '@/compositions/useUser';
import useVacancies from '@/compositions/useVacancies';
import useCandidates from '@/compositions/useCandidates';
import { useKanbanMultipleOperations } from '@/compositions/useKanbanMultipleOperations';

export default {
  name: 'SendMessageWithTemplateForm',
  props: {
    value: {
      type: String,
      default: '',
    },
    channel: {
      type: String,
      default: notificationChannelType.EMAIL,
    },
  },
  setup(props, { emit }) {
    const { channel, value } = toRefs(props);
    const textarea = ref(null);
    const template = ref(value.value || '');
    const { t } = useI18n();
    const variables = [
      {
        id: 'company.name',
        name: t('companyName'),
      },
      {
        id: 'vacancy.title',
        name: t('vacancyTitle'),
      },
      {
        id: 'hr.firstName',
        name: t('myFirstName'),
      },
      {
        id: 'hr.lastName',
        name: t('myLastName'),
      },
      {
        id: 'hr.phone',
        name: t('myPhone'),
      },
      {
        id: 'hr.email',
        name: t('myEmail'),
      },
      {
        id: 'vacancy.link',
        name: t('vacancyLink'),
      },
    ];

    const {
      smsLimitExceeded, calculateMessage, smsCharactersCount, smsCharactersLimit,
    } = useSms();

    watch(value, (val) => {
      const nextValue = val || '';

      if (nextValue !== template.value) {
        template.value = nextValue;
      }
    });

    watch(template, () => {
      if (channel.value === notificationChannelType.SMS) {
        calculateMessage(template.value);
      }

      emit('input', template.value);
    });

    const { company, currentUser } = useUser();
    const { currentVacancy } = useVacancies();
    const { findCandidateById, candidates } = useCandidates();
    const { selectedCandidates } = useKanbanMultipleOperations();

    const selectedCandidate = computed(() => {
      const selectedCandidateId = selectedCandidates.value?.[0];

      if (!selectedCandidateId) {
        return null;
      }

      const directCandidate = findCandidateById(selectedCandidateId)
        || findCandidateById(Number(selectedCandidateId));

      if (directCandidate) {
        return directCandidate;
      }

      const normalizedSelectedId = String(selectedCandidateId);

      return candidates.value.find((candidate) => String(candidate.id) === normalizedSelectedId) || null;
    });

    const resolvedVacancy = computed(() => selectedCandidate.value?.vacancy || currentVacancy.value || null);

    function insertVariableAtCursorPosition(variable) {
      const textareaElement = textarea.value.$el.querySelector('textarea');
      const currentValue = template.value || '';
      let text = `{${variable.id}}`;
      const start = textareaElement.selectionStart;
      const end = textareaElement.selectionEnd;

      const variableValue = getVariableValue(
        variable.id,
        currentValue,
        currentUser.value,
        company.value,
        resolvedVacancy.value,
        selectedCandidate.value,
      );

      text = variableValue || text;

      template.value = `${currentValue.slice(0, start)}${text}${currentValue.slice(end)}`;

      nextTick(() => {
        textareaElement.setSelectionRange(start + text.length, start + text.length);
      });
    }

    return {
      vars: computed(() => variables),
      textarea,
      template,
      smsCharactersCount,
      smsCharactersLimit,
      insertVariableAtCursorPosition,
      error: computed(() => {
        if (channel.value === notificationChannelType.SMS) {
          return smsLimitExceeded.value ? t('smsLimitExceeded') : false;
        }
        return false;
      }),
    };
  },
};
</script>

<style scoped>
.var-item {
  line-height: 1em;
}
.smsCounter {
  color:#000;
}
.smsCounter.exceed {
  color:#cc0000;
  background: none;
}
</style>

<i18n locale="en" lang="yaml">
  insertVariable: Insert template variable
  save: Save
  companyName: Company name
  vacancyTitle: Position
  candidateFirstName: Candidate first name
  candidateLastName: Candidate last name
  myFirstName: My first name
  myLastName: My last name
  myPhone: My phone number
  myEmail: My e-mail
  interviewLink: Interview link
  vacancyLink: Job link
  smsLimitExceeded: Message limit exceeded
  usedSymbolLimit: Used {smsCharactersCount} out of a {smsCharactersLimit} characters limit
</i18n>

<i18n locale="ru" lang="yaml">
  insertVariable: Вставить переменную
  save: Сохранить
  companyName: Компания
  vacancyTitle: Вакансия
  candidateFirstName: Имя соискателя
  candidateLastName: Фамилия соискателя
  myFirstName: Моё имя
  myLastName: Моя фамилия
  myPhone: Мой телефон
  myEmail: Моя почта
  interviewLink: Ссылка на интервью
  vacancyLink: Ссылка на вакансию
  smsLimitExceeded: Превышена длина сообщения
  usedSymbolLimit: Использовано {smsCharactersCount} из {smsCharactersLimit} символов
</i18n>
